import React, { useState } from "react";
import dayjs from "dayjs";
import parse from "html-react-parser";

import {
  Button,
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ThemeProvider,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLineUpDown, faAbacus } from "@fortawesome/pro-solid-svg-icons";

import {
  LoadingButton,
  TextField,
  AnimatedLogo,
  Checkbox,
  Select
} from "@aclymatepackages/atoms";
import { sumTonsCo2e } from "@aclymatepackages/other-helpers";
import { ucFirstLetters } from "@aclymatepackages/formatters";
import { filterByDateRange } from "@aclymatepackages/date-helpers";
import { mergeDarkTheme } from "@aclymatepackages/themes";

import CollapsibleAlert from "../../atoms/notifications/CollapsibleAlert";
import SlideLayout from "../../layouts/SlideLayout";

import {
  useCachedFirebaseCrud,
  useAccountData
} from "../../../helpers/firebase";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import { useFindAccountingBlockingEmissions } from "../../../helpers/utils/emissions";
import { formatBlockerMessage } from "../../../helpers/utils/accountingBlockers";
import useAccountingData from "../../../helpers/hooks/accountingData";
import { analyticsTrack } from "../../../helpers/analytics";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";

const buildYearsInRange = (startYear, endYear) =>
  Array(Math.max(endYear - startYear + 1, 0))
    .fill()
    .map((_, idx) => startYear + idx);

const AccountingChecklistStep = ({ isChecked, title, disabled, onCheck }) => {
  return (
    <Grid item>
      <Checkbox
        label={title}
        value={isChecked}
        disabled={disabled}
        editValue={onCheck}
      />
    </Grid>
  );
};

const AccountingSlideLayoutContent = ({
  actionText,
  setOpen,
  dataLoading,
  accountingYear,
  isBaselineInput,
  startYear,
  lastCompletedYear
}) => {
  const {
    monthlyCommuteEmissionsTons,
    monthlyUtilitiesEmissionsTons,
    separateUtilitiesOfficesDefaultEmissions,
    allEmissions
  } = useEmissionsContext();

  const findAccountingBlockingEmissions = useFindAccountingBlockingEmissions();

  const [{ unClosedYears = [{}] }] = useAccountingData();
  const { newCollectionDoc, updateAccountData } = useCachedFirebaseCrud();

  const [saveDataLoading, setSaveDataLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [checkedStep, setCheckedStep] = useState(0);
  const [selectedBaselineYear, setSelectedBaselineYear] =
    useState(accountingYear);

  const effectiveYear = isBaselineInput ? selectedBaselineYear : accountingYear;
  const accountingDate = new Date(effectiveYear, 11, 31);

  const hasCompletedYear = lastCompletedYear >= startYear;

  const baselineYearOptions =
    isBaselineInput && hasCompletedYear
      ? buildYearsInRange(startYear, lastCompletedYear).map((year) => ({
          label: `${year}`,
          value: year
        }))
      : [];

  const preBaselineYears = isBaselineInput
    ? buildYearsInRange(startYear, selectedBaselineYear).filter(
        (year) => year !== selectedBaselineYear
      )
    : [];

  const disclaimers = [
    `All of my emissions are complete and accurate through ${effectiveYear}.`,
    `All of the information for my offices is complete and correct for ${effectiveYear}.`,
    `All of my employees and their commute schedules are complete and accurate for ${effectiveYear}.`
  ];

  const { isBlocked, blockers = [] } =
    findAccountingBlockingEmissions(accountingDate);

  const allChecked = checkedStep === disclaimers.length;

  const saveAccountingYear = async (year, isBaseline) => {
    const recurringEmissionsThisYear = [
      ...monthlyCommuteEmissionsTons,
      ...monthlyUtilitiesEmissionsTons,
      ...separateUtilitiesOfficesDefaultEmissions
    ].filter(({ date }) => dayjs(date).year() === year);

    await Promise.all(
      recurringEmissionsThisYear.map((emission) =>
        newCollectionDoc("transactions", {
          ...emission,
          dateCreated: new Date(),
          source: "recurring-closed",
          archived: false
        })
      )
    );

    const monthsArray = [...new Array(12)].map((_, idx) => {
      const monthStartDate = dayjs(new Date(year, idx, 1));
      const daysInMonth = dayjs(monthStartDate).daysInMonth();
      return {
        startDate: monthStartDate,
        endDate: dayjs(new Date(year, idx, daysInMonth))
      };
    });

    const monthlySubcategoryEmissionsTons = monthsArray.map(
      ({ startDate, endDate }) => {
        const emissionsThisMonth = allEmissions.filter((emission) =>
          filterByDateRange({
            filterStartDate: startDate,
            filterEndDate: endDate,
            ...emission
          })
        );

        const uniqueEmissionSubcategories = [
          ...new Set(emissionsThisMonth.map(({ subcategory }) => subcategory))
        ];

        return Object.fromEntries(
          uniqueEmissionSubcategories.map((subcategory) => {
            const subcategoryEmissions = emissionsThisMonth.filter(
              (emission) => emission.subcategory === subcategory
            );
            const subcategoryTonsCo2e = sumTonsCo2e(subcategoryEmissions);
            return [subcategory, subcategoryTonsCo2e];
          })
        );
      }
    );

    await newCollectionDoc("accounting", {
      date: new Date(year, 11, 31),
      baseline: isBaseline,
      monthlySubcategoryEmissionsTons
    });

    return recurringEmissionsThisYear.length > 0;
  };

  const onSave = async () => {
    setSaveDataLoading(true);

    const yearsToClose = isBaselineInput
      ? buildYearsInRange(startYear, selectedBaselineYear)
      : [accountingYear];

    const recurringCreatedFlags = await Promise.all(
      yearsToClose.map((year) =>
        saveAccountingYear(
          year,
          isBaselineInput && year === selectedBaselineYear
        )
      )
    );

    if (recurringCreatedFlags.some(Boolean)) {
      await updateAccountData({ hasUnaggregatedTransactions: true });
    }

    analyticsTrack(isBaselineInput ? "Baseline Set" : "Closed Acct Year", {
      year: effectiveYear
    });

    if (
      (!isBaselineInput && unClosedYears.length === 1) ||
      (isBaselineInput && dayjs().year() - selectedBaselineYear > 0)
    ) {
      analyticsTrack("Silver Unlock");
    }
    return setOpen(false);
  };

  const isProduction = process.env.REACT_APP_ENVIRONMENT === "production";

  if (dataLoading) {
    return <AnimatedLogo />;
  }

  if (isBaselineInput && !hasCompletedYear) {
    return (
      <CollapsibleAlert
        severity="info"
        title="You need a full year of data before setting a baseline"
        subtitle="You can set your baseline once you've measured a complete calendar year of emissions."
      />
    );
  }

  const alertTitle = `Resolve these items before you can ${actionText}`;
  const alertSubtitle = (
    <List dense disablePadding>
      {blockers.map((blocker, idx) => (
        <ListItem
          key={`accounting-blocker-${idx}`}
          disableGutters
          sx={{ paddingTop: 0, paddingBottom: 0 }}
        >
          <ListItemText
            primary={formatBlockerMessage(blocker)}
            primaryTypographyProps={{ variant: "caption" }}
          />
        </ListItem>
      ))}
    </List>
  );

  if (isBlocked && isProduction) {
    return (
      <CollapsibleAlert
        title={alertTitle}
        subtitle={alertSubtitle}
        severity="warning"
      />
    );
  }

  return (
    <Grid container spacing={2} direction="column">
      {isBlocked && (
        <Grid item>
          <CollapsibleAlert
            severity="warning"
            title={alertTitle}
            subtitle={alertSubtitle}
          />
        </Grid>
      )}
      {isBaselineInput && (
        <Grid item>
          <Select
            label="Baseline year"
            options={baselineYearOptions}
            value={selectedBaselineYear}
            editValue={(value) => setSelectedBaselineYear(Number(value))}
            size="small"
          />
        </Grid>
      )}
      {!!preBaselineYears.length && (
        <Grid item>
          <CollapsibleAlert
            severity="info"
            title={`${preBaselineYears.join(", ")} will be closed as pre-baseline year${
              preBaselineYears.length > 1 ? "s" : ""
            }`}
            subtitle="These earlier years will be locked and tracked, but won't count toward your baseline comparison."
          />
        </Grid>
      )}
      <Grid item>
        <Typography variant="body1">
          Please confirm the following information to {actionText} for the year{" "}
          {effectiveYear}
        </Typography>
      </Grid>
      {disclaimers.map((disclaimer, idx) => (
        <AccountingChecklistStep
          key={`accounting-checklist-step-${idx}`}
          title={disclaimer}
          isChecked={idx < checkedStep}
          disabled={checkedStep > idx || idx > checkedStep}
          onCheck={() => setCheckedStep((currentStep) => currentStep + 1)}
        />
      ))}
      {allChecked && (
        <Grid
          item
          container
          spacing={2}
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            <TextField
              label="Confirmation"
              helperText={`Please confirm that you understand by typing "I understand"`}
              value={confirmationText}
              setValue={setConfirmationText}
            />
          </Grid>
          <Grid item>
            <LoadingButton
              isLoading={saveDataLoading}
              label={actionText}
              variant="contained"
              color="primary"
              disabled={confirmationText.toLowerCase() !== "i understand"}
              onClick={onSave}
              size="small"
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const LeftDisclaimerCopy = () => {
  const { palette } = useTheme();

  const sections = [
    {
      title:
        "Closing a year finalizes your emissions data and sets a baseline for future comparison.",
      subtitle: "A closed year:",
      bullets: [
        "Locks emissions data",
        "Enables year-over-year tracking",
        "Supports consistent reporting"
      ]
    },
    {
      title: "Before you Close:",
      subtitle:
        "Make sure your emissions data is as <strong>complete and accurate as possible.</strong> This includes:",
      bullets: [
        "All known activity data entered",
        "Emissions categorizations reviewed and finalized",
        "Any outstanding gaps addressed or intentionally estimated",
        "Once a year is closed, <strong>it cannot be edited</strong>"
      ]
    },
    {
      title: "Best Practice",
      subtitle:
        "Only close your carbon accounting year when you're confident the data represents your best understanding of that year's emissions. This ensures your reporting, targets, and progress tracking are built on a solid foundation."
    }
  ];

  return (
    <Grid direction="column" spacing={4} container>
      <Grid item>
        <Typography variant="h3">
          What does it mean to close your account?
        </Typography>
      </Grid>
      {sections.map(({ title, subtitle, bullets }, idx) => (
        <Grid item key={`section-${idx}`}>
          <Typography variant="h5" color="textPrimary">
            {title}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {parse(subtitle)}
          </Typography>
          {bullets && (
            <List sx={{ listStyleType: "disc", pl: 2 }}>
              {bullets.map((bullet, bulletIdx) => (
                <ListItem
                  key={`bullet-${bulletIdx}`}
                  sx={{
                    display: "list-item",
                    p: 0,
                    "&::marker": { color: palette.text.secondary }
                  }}
                >
                  <ListItemText
                    primary={parse(bullet)}
                    sx={{ color: palette.text.secondary }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

const AccountingSlideLayout = ({ setOpen, type, ...otherProps }) => {
  const { palette } = useTheme();

  const isBaselineInput = type === "baseline";
  const actionText = isBaselineInput
    ? "set your baseline"
    : "close your accounting";

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={setOpen}
      header={
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar style={{ backgroundColor: palette.secondary.main }}>
              <FontAwesomeIcon
                icon={isBaselineInput ? faChartLineUpDown : faAbacus}
                style={{ color: "white" }}
                size="lg"
              />
            </Avatar>
          </Grid>
          <Grid item>
            <Typography variant="h4">{ucFirstLetters(actionText)}</Typography>
          </Grid>
        </Grid>
      }
      content={
        <AccountingSlideLayoutContent
          isBaselineInput={isBaselineInput}
          actionText={actionText}
          setOpen={setOpen}
          {...otherProps}
        />
      }
      leftContent={
        <ThemeProvider theme={mergeDarkTheme()}>
          <LeftDisclaimerCopy />
        </ThemeProvider>
      }
    />
  );
};

const BaselineSlider = ({ setOpen }) => {
  const [{ startDate }, accountDataLoading] = useAccountData();

  const startYear = dayjs(startDate).year();
  const lastCompletedYear = dayjs().year() - 1;

  return (
    <AccountingSlideLayout
      setOpen={setOpen}
      type="baseline"
      dataLoading={accountDataLoading}
      accountingYear={startYear}
      startYear={startYear}
      lastCompletedYear={lastCompletedYear}
    />
  );
};

const CloseAccountingSlider = ({ setOpen }) => {
  const [{ unClosedYears = [{}] }] = useAccountingData();

  const [{ date }] = unClosedYears;
  const year = dayjs(date).year();

  return (
    <AccountingSlideLayout
      setOpen={setOpen}
      type="accounting"
      accountingYear={year}
    />
  );
};

const CloseAccountingButton = ({
  type,
  color = "primary",
  disabled,
  accountingYear,
  size = "medium",
  ButtonComponent
}) => {
  const { isSimulation } = usePlatformLayoutContext();
  const { isAdmin } = usePermissions();

  const [accountingActionModalOpen, setAccountingActionModalOpen] =
    useState(false);

  const dialogBoxTypes = {
    baseline: <BaselineSlider setOpen={setAccountingActionModalOpen} />,
    accounting: (
      <CloseAccountingSlider
        setOpen={setAccountingActionModalOpen}
        accountingYear={accountingYear}
      />
    )
  };

  const inputBox = dialogBoxTypes[type];

  const buttonProps = {
    onClick: () => setAccountingActionModalOpen(true),
    disabled: disabled || isSimulation || !isAdmin
  };

  const button = ButtonComponent ? (
    <ButtonComponent {...buttonProps} />
  ) : (
    <Button
      key="close-accounting-button"
      variant="contained"
      color={color}
      size={size}
      {...buttonProps}
    >
      {type === "baseline" ? "set your baseline" : "close your accounting"}
    </Button>
  );

  return (
    <>
      {accountingActionModalOpen && inputBox}
      {button}
    </>
  );
};
export default CloseAccountingButton;
